import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';

import { useHistory } from 'react-router-dom';
import { FiTruck } from 'react-icons/fi';
import { MdSearch } from 'react-icons/md';
import { IPaginate } from 'shared/interfaces';
import { useToast } from 'contexts';
import { ModalHandles } from 'components/Modal';

import {
  getAjustesEstoque,
  deleteAjusteEstoque,
} from 'services/api/ajusteEstoque';

import * as C from 'components';
import * as S from './styles';

import { IAjusteEstoque } from '../interfaces';

export const EnvioAjusteEstoqueList: React.FC = () => {
  const { addToast } = useToast();
  const history = useHistory();
  const [ajustes, setAjustes] = useState<IPaginate<IAjusteEstoque>>();
  const [ajusteAtual, setAjusteAtual] = useState<IAjusteEstoque>();
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');
  const modalRef = useRef<ModalHandles>(null);
  const [modalType, setModalType] = useState('remove');

  const handleSearch = useCallback(
    ({ page, per_page }) => {
      setLoading(true);

      const params = {
        page,
        per_page,
        term,
      };

      getAjustesEstoque(params)
        .then((data) => {
          setAjustes(data);

          setLoading(false);
        })
        .catch((e) => {
          addToast({
            type: 'error',
            title: 'Ops, Erro',
            description: e.response?.data?.message,
          });

          setLoading(false);
        });
    },
    [addToast, term],
  );

  useEffect(() => {
    const params = {
      page: 1,
      per_page: 20,
    };

    handleSearch(params);
  }, [handleSearch]);

  const handleRemove = useCallback(async () => {
    try {
      await deleteAjusteEstoque(ajusteAtual!.id);

      if (!ajustes) return;

      const newList = ajustes.data.filter(
        (item) => item.id !== ajusteAtual?.id,
      );
      setAjustes({ ...ajustes, data: newList });

      modalRef.current?.closeModal();

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Registro removido com sucesso',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Ops, Erro',
        description: 'Ocorreu um erro ao remover o registro',
      });
    }
  }, [addToast, ajusteAtual, ajustes]);

  const handleOpenModal = useCallback(
    (ajusteSelected: IAjusteEstoque, type: string): void => {
      setModalType(type);

      setAjusteAtual(ajusteSelected);

      modalRef.current?.openModal();
    },
    [],
  );

  const renderModal = useMemo(() => {
    if (modalType === 'remove') {
      return (
        <C.Modal ref={modalRef} options={{ width: '400px' }}>
          <C.ModalHeader>
            <C.ModalTitle>Você Confirma a EXCLUSÃO?</C.ModalTitle>
          </C.ModalHeader>
          <C.ModalBody>
            <p>O registro será removido completamente!</p>
          </C.ModalBody>
          <C.ModalFooter>
            <C.Position align="right">
              <C.Button
                variant="outline-secondary"
                onClick={() => modalRef.current?.closeModal()}
              >
                Cancelar
              </C.Button>
              <C.Button
                variant="primary"
                onClick={() => handleRemove()}
                style={{ marginLeft: '16px' }}
              >
                SIM, confirmo a exclusão
              </C.Button>
            </C.Position>
          </C.ModalFooter>
        </C.Modal>
      );
    }

    return <></>;
  }, [handleRemove, modalType]);

  return (
    <S.Container>
      <C.HeaderBackground>
        <C.HeaderPage>
          <C.HeaderInfo title="Ajuste estoque" icon={FiTruck} />

          <S.ContainerSearch>
            <C.InputSearch
              title="Digite aqui o que deseja pesquisar"
              icon={MdSearch}
              placeholder="Pesquisar"
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => {
                e.key === 'Enter'
                  ? handleSearch({
                      page: 1,
                      per_page: ajustes?.per_page,
                    })
                  : {};
              }}
            />
          </S.ContainerSearch>

          <div>
            <C.Can checkPermission="ajuste_estoque_create">
              <C.ButtonLink title="Adicionar Estoque" to="/ajuste-estoque/new">
                Adicionar
              </C.ButtonLink>
            </C.Can>
          </div>
        </C.HeaderPage>
      </C.HeaderBackground>

      <S.Panel>
        {loading ? (
          <C.Loader loading={loading} align="center" message="Carregando..." />
        ) : (
          <>
            <div className="tbl-content">
              <C.Table>
                <thead>
                  <tr>
                    <th className="text-center">Data</th>
                    <th className="text-left">Tipo</th>
                    <th className="text-left">Produto</th>
                    <th className="text-center">Quantidade</th>
                    <th className="text-center">Usuário</th>
                    <th className="text-center">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {ajustes &&
                    ajustes.data.map((item: IAjusteEstoque) => (
                      <tr key={item.id}>
                        <td className="text-center">{item.created_at}</td>
                        <td className="text-left">{item.tipo}</td>
                        <td>{item.produto.nome}</td>
                        <td className="text-center">{item.quantidade}</td>
                        <td className="text-center">
                          {item.user_created?.name}
                        </td>

                        <td className="text-center">
                          <C.PopUpMenu>
                            <C.Can checkPermission="ajuste_estoque_update">
                              <button
                                type="button"
                                onClick={() =>
                                  history.push(
                                    `/ajuste-estoque/edit/${item.id}`,
                                  )
                                }
                              >
                                <S.IconEdit
                                  title="Editar"
                                  size={24}
                                  color="#707070"
                                />
                                Editar
                              </button>
                            </C.Can>

                            <C.Can checkPermission="ajuste_estoque_delete">
                              <button
                                type="button"
                                onClick={() => handleOpenModal(item, 'remove')}
                              >
                                <S.IconDelete
                                  title="Remover"
                                  size={24}
                                  color="#707070"
                                />
                                Remover
                              </button>
                            </C.Can>
                          </C.PopUpMenu>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </C.Table>
            </div>
          </>
        )}
        <S.PaginationContainer>
          {ajustes && (
            <C.Pagination
              page={ajustes?.page}
              total={ajustes?.total}
              per_page={ajustes?.per_page}
              last_page={ajustes?.last_page}
              onSearch={handleSearch}
            />
          )}
        </S.PaginationContainer>
      </S.Panel>

      {renderModal}
    </S.Container>
  );
};
